/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { EmptyValue } from '../../interpreter/InterpreterValue';
import { MatrixVertex, ValueCellVertex } from '../index';
import { NoSheetWithIdError } from '../../errors';
export class AddressMapping {
    constructor(policy) {
        this.policy = policy;
        this.mapping = new Map();
    }
    /** @inheritDoc */
    getCell(address) {
        const sheetMapping = this.mapping.get(address.sheet);
        if (!sheetMapping) {
            throw new NoSheetWithIdError(address.sheet);
        }
        return sheetMapping.getCell(address);
    }
    fetchCell(address) {
        const sheetMapping = this.mapping.get(address.sheet);
        if (!sheetMapping) {
            throw new NoSheetWithIdError(address.sheet);
        }
        const vertex = sheetMapping.getCell(address);
        if (!vertex) {
            throw Error('Vertex for address missing in AddressMapping');
        }
        return vertex;
    }
    strategyFor(sheetId) {
        const strategy = this.mapping.get(sheetId);
        if (!strategy) {
            throw new NoSheetWithIdError(sheetId);
        }
        return strategy;
    }
    addSheet(sheetId, strategy) {
        if (this.mapping.has(sheetId)) {
            throw Error('Sheet already added');
        }
        this.mapping.set(sheetId, strategy);
    }
    autoAddSheet(sheetId, sheet, sheetBoundaries) {
        const { height, width, fill } = sheetBoundaries;
        const strategyConstructor = this.policy.call(fill);
        this.addSheet(sheetId, new strategyConstructor(width, height));
    }
    getCellValue(address) {
        const vertex = this.getCell(address);
        if (vertex === null) {
            return EmptyValue;
        }
        else if (vertex instanceof MatrixVertex) {
            return vertex.getMatrixCellValue(address);
        }
        else {
            return vertex.getCellValue();
        }
    }
    getRawValue(address) {
        const vertex = this.getCell(address);
        if (vertex instanceof ValueCellVertex) {
            return vertex.getValues().rawValue;
        }
        else if (vertex instanceof MatrixVertex) {
            return vertex.getMatrixCellRawValue(address);
        }
        else {
            return null;
        }
    }
    /** @inheritDoc */
    setCell(address, newVertex) {
        const sheetMapping = this.mapping.get(address.sheet);
        if (!sheetMapping) {
            throw Error('Sheet not initialized');
        }
        sheetMapping.setCell(address, newVertex);
    }
    removeCell(address) {
        const sheetMapping = this.mapping.get(address.sheet);
        if (!sheetMapping) {
            throw Error('Sheet not initialized');
        }
        sheetMapping.removeCell(address);
    }
    /** @inheritDoc */
    has(address) {
        const sheetMapping = this.mapping.get(address.sheet);
        if (!sheetMapping) {
            return false;
        }
        return sheetMapping.has(address);
    }
    /** @inheritDoc */
    getHeight(sheetId) {
        const sheetMapping = this.mapping.get(sheetId);
        if (!sheetMapping) {
            throw new NoSheetWithIdError(sheetId);
        }
        return sheetMapping.getHeight();
    }
    /** @inheritDoc */
    getWidth(sheetId) {
        const sheetMapping = this.mapping.get(sheetId);
        if (!sheetMapping) {
            throw new NoSheetWithIdError(sheetId);
        }
        return sheetMapping.getWidth();
    }
    addRows(sheet, row, numberOfRows) {
        const sheetMapping = this.mapping.get(sheet);
        if (!sheetMapping) {
            throw Error('Sheet does not exist');
        }
        sheetMapping.addRows(row, numberOfRows);
    }
    removeRows(removedRows) {
        const sheetMapping = this.mapping.get(removedRows.sheet);
        if (!sheetMapping) {
            throw Error('Sheet does not exist');
        }
        sheetMapping.removeRows(removedRows);
    }
    removeSheet(sheetId) {
        this.mapping.delete(sheetId);
    }
    addColumns(sheet, column, numberOfColumns) {
        const sheetMapping = this.mapping.get(sheet);
        if (!sheetMapping) {
            throw Error('Sheet does not exist');
        }
        sheetMapping.addColumns(column, numberOfColumns);
    }
    removeColumns(removedColumns) {
        const sheetMapping = this.mapping.get(removedColumns.sheet);
        if (!sheetMapping) {
            throw Error('Sheet does not exist');
        }
        sheetMapping.removeColumns(removedColumns);
    }
    *verticesFromRowsSpan(rowsSpan) {
        yield* this.mapping.get(rowsSpan.sheet).verticesFromRowsSpan(rowsSpan); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }
    *verticesFromColumnsSpan(columnsSpan) {
        yield* this.mapping.get(columnsSpan.sheet).verticesFromColumnsSpan(columnsSpan); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }
    *entriesFromRowsSpan(rowsSpan) {
        yield* this.mapping.get(rowsSpan.sheet).entriesFromRowsSpan(rowsSpan);
    }
    *entriesFromColumnsSpan(columnsSpan) {
        yield* this.mapping.get(columnsSpan.sheet).entriesFromColumnsSpan(columnsSpan);
    }
    *entries() {
        for (const [sheet, mapping] of this.mapping.entries()) {
            yield* mapping.getEntries(sheet);
        }
    }
    *sheetEntries(sheet) {
        const sheetMapping = this.mapping.get(sheet);
        if (sheetMapping) {
            yield* sheetMapping.getEntries(sheet);
        }
        else {
            throw new Error('Sheet does not exists');
        }
    }
    destroy() {
        this.mapping.clear();
    }
}
